import { Link } from '@tanstack/react-router'
import { Badge } from '@workspace/ui/components/badge'
import { Button } from '@workspace/ui/components/button'
import type { TableColumn } from '@workspace/ui/components/data-table'
import { CellText, DataTable } from '@workspace/ui/components/data-table'
import { Fragment, useState } from 'react'
import { useAdminAgentOverviewQuery, useAdminAgentSessionCallsQuery } from '../hooks/use-admin-api'

const PAGE_SIZE = 25

type Overview = NonNullable<ReturnType<typeof useAdminAgentOverviewQuery>['data']>['data']
type Session = Overview['sessions'][number]
type Tool = 'FIND' | 'READ' | 'CHECK' | 'NOTE' | 'JOURNAL' | 'THESIS'

const formatTime = (value: Date | string) =>
  new Date(value).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })

const formatCallTime = (value: Date | string) =>
  new Date(value).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })

const formatLatency = (ms: number) => (ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`)

function duration(session: Session) {
  const seconds = Math.round(
    (new Date(session.endedAt).getTime() - new Date(session.startedAt).getTime()) / 1000
  )
  return seconds < 60 ? `${seconds} s` : `${Math.round(seconds / 60)} min`
}

function formatReply(text: string): string {
  try {
    return JSON.stringify(JSON.parse(text), null, 2)
  } catch {
    return text
  }
}

function SessionCalls({ sessionId }: { sessionId: string }) {
  const { data, isLoading } = useAdminAgentSessionCallsQuery(sessionId, true)
  const [expanded, setExpanded] = useState<string | null>(null)
  if (isLoading) return <CellText>Loading calls...</CellText>
  const calls = data?.data.calls ?? []
  return (
    <table className="w-full text-xs">
      <thead className="text-left text-muted-foreground">
        <tr>
          <th className="py-1 font-medium">Time</th>
          <th className="py-1 font-medium">Tool</th>
          <th className="py-1 font-medium">Wording</th>
          <th className="py-1 font-medium">Latency</th>
          <th className="py-1 font-medium">Status</th>
          <th className="py-1 font-medium">Refused</th>
        </tr>
      </thead>
      <tbody>
        {calls.map((call) => {
          const key = `${call.createdAt}-${call.tool}`
          return (
            <Fragment key={key}>
              <tr
                className="cursor-pointer border-t border-border"
                onClick={() => setExpanded(expanded === key ? null : key)}
              >
                <td className="py-1">{formatCallTime(call.createdAt)}</td>
                <td className="py-1">{call.tool.toLowerCase()}</td>
                <td className="py-1 font-mono">{call.textsVersion ?? '-'}</td>
                <td className="py-1">{formatLatency(call.latencyMs)}</td>
                <td className="py-1">{call.statusCode}</td>
                <td className="py-1">
                  {call.refused ? call.refused.replaceAll('_', ' ').toLowerCase() : '-'}
                </td>
              </tr>
              {expanded === key && (
                <tr>
                  <td colSpan={6} className="pb-2">
                    <p className="py-1 text-muted-foreground">Sent by the assistant:</p>
                    <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-words rounded bg-muted p-2">
                      {JSON.stringify(call.arguments ?? {}, null, 2)}
                    </pre>
                    <p className="py-1 text-muted-foreground">Our reply:</p>
                    <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-words rounded bg-muted p-2">
                      {call.resultText
                        ? formatReply(call.resultText)
                        : 'Not kept (older than 90 days).'}
                    </pre>
                  </td>
                </tr>
              )}
            </Fragment>
          )
        })}
      </tbody>
    </table>
  )
}

/** Sessions of investors' assistants, expandable to their calls; for one firm when `investorId` is given. */
export function AgentSessionsTable({ tool, investorId }: { tool?: Tool; investorId?: string }) {
  const [page, setPage] = useState(1)
  const [open, setOpen] = useState<string | null>(null)
  const query = useAdminAgentOverviewQuery({ tool, investorId, page, limit: PAGE_SIZE })
  const data = query.data?.data
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1

  const sessionColumns: TableColumn<Session>[] = [
    {
      header: 'Investor',
      render: (item) => (
        <div className="space-y-0.5">
          <Link
            to="/admin/users/$id"
            params={{ id: item.investor.userId }}
            className="text-sm font-medium text-primary hover:underline"
          >
            {item.investor.name ?? item.investor.email}
          </Link>
          {item.firmId ? (
            <Link
              to="/admin/investors/$id"
              params={{ id: item.firmId }}
              className="block text-xs text-primary hover:underline"
            >
              {item.firm}
            </Link>
          ) : (
            <CellText className="block text-xs">{item.firm ?? 'No firm'}</CellText>
          )}
        </div>
      ),
    },
    {
      header: 'Client',
      width: '150px',
      render: (item) => <CellText>{item.clientName ?? '-'}</CellText>,
    },
    {
      header: 'Started',
      width: '150px',
      render: (item) => <CellText>{formatTime(item.startedAt)}</CellText>,
    },
    { header: 'Duration', width: '100px', render: (item) => <CellText>{duration(item)}</CellText> },
    { header: 'Calls', width: '80px', render: (item) => <CellText>{item.calls}</CellText> },
    {
      header: 'Refusals',
      width: '100px',
      render: (item) =>
        item.refusals > 0 ? (
          <Badge variant="outline">{item.refusals}</Badge>
        ) : (
          <CellText>0</CellText>
        ),
    },
    {
      header: '',
      width: '110px',
      render: (item) => (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setOpen(open === item.sessionId ? null : item.sessionId)}
        >
          {open === item.sessionId ? 'Hide calls' : 'Show calls'}
        </Button>
      ),
    },
  ]

  return (
    <div className="space-y-2">
      <DataTable
        columns={sessionColumns}
        data={data?.sessions ?? []}
        isLoading={query.isLoading}
        emptyMessage="No sessions in this window."
        keyExtractor={(item) => item.sessionId}
        minWidth="900px"
        renderExpanded={(item) =>
          open === item.sessionId ? <SessionCalls sessionId={item.sessionId} /> : null
        }
      />
      <div className="flex items-center justify-end gap-2 text-sm text-muted-foreground">
        <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>
          Previous
        </Button>
        <span>
          Page {page} of {totalPages}
        </span>
        <Button
          size="sm"
          variant="outline"
          disabled={page >= totalPages}
          onClick={() => setPage(page + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  )
}
